import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CoinsIcon, TrendingUpIcon, TrendingDownIcon } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface CoinFlipStats {
  flips: number;
  wins: number;
  losses: number;
  streak: number;
  bestStreak: number;
}

export const CoinFlipGame = () => {
  const { toast } = useToast();
  const [isFlipping, setIsFlipping] = useState(false);
  const [prediction, setPrediction] = useState<"heads" | "tails" | null>(null);
  const [result, setResult] = useState<"heads" | "tails" | null>(null);
  const [stats, setStats] = useState<CoinFlipStats>({
    flips: 0,
    wins: 0,
    losses: 0,
    streak: 0,
    bestStreak: 0
  });

  const handlePrediction = (choice: "heads" | "tails") => {
    if (isFlipping) return;
    setPrediction(choice);
  };

  const flipCoin = async () => {
    if (!prediction) {
      toast({
        title: "Make a prediction",
        description: "Choose heads or tails before flipping!",
      });
      return;
    }

    setIsFlipping(true);
    // Simulate coin flip animation time
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const outcome = Math.random() < 0.5 ? "heads" : "tails";
    setResult(outcome);
    
    // Update stats
    const won = prediction === outcome;
    setStats(prev => {
      const newStreak = won ? prev.streak + 1 : 0;
      return {
        flips: prev.flips + 1,
        wins: won ? prev.wins + 1 : prev.wins,
        losses: won ? prev.losses : prev.losses + 1,
        streak: newStreak,
        bestStreak: Math.max(prev.bestStreak, newStreak)
      };
    });

    // Show result toast
    toast({
      title: won ? "You won! 🎉" : "Try again!",
      description: `The coin landed on ${outcome}`,
    });

    setIsFlipping(false);
    setPrediction(null);
  };

  const getWinRate = () => {
    if (stats.flips === 0) return 0;
    return Math.round((stats.wins / stats.flips) * 100);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 p-4 animate-fade-in">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CoinsIcon className="w-6 h-6" />
            Coin Flip Challenge
          </CardTitle>
          <CardDescription>
            Test your luck and learn about probability in investing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Game Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold">{stats.flips}</div>
                <div className="text-sm text-muted-foreground">Total Flips</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold">{getWinRate()}%</div>
                <div className="text-sm text-muted-foreground">Win Rate</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold">{stats.streak}</div>
                <div className="text-sm text-muted-foreground">Current Streak</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold">{stats.bestStreak}</div>
                <div className="text-sm text-muted-foreground">Best Streak</div>
              </CardContent>
            </Card>
          </div>

          {/* Coin Display */}
          <div className="flex justify-center">
            <div 
              className={`
                w-32 h-32 rounded-full bg-secondary/20 flex items-center justify-center
                ${isFlipping ? 'animate-[spin_1s_ease-in-out]' : ''}
                ${result ? 'scale-100' : 'scale-90'}
                transition-all duration-300
              `}
            >
              <CoinsIcon 
                className={`
                  w-16 h-16 text-secondary
                  ${isFlipping ? 'opacity-0' : 'opacity-100'}
                  transition-opacity duration-300
                `} 
              />
            </div>
          </div>

          {/* Prediction Buttons */}
          <div className="flex justify-center gap-4">
            <Button
              variant={prediction === "heads" ? "default" : "outline"}
              onClick={() => handlePrediction("heads")}
              disabled={isFlipping}
              className="w-32"
            >
              <TrendingUpIcon className="w-4 h-4 mr-2" />
              Heads
            </Button>
            <Button
              variant={prediction === "tails" ? "default" : "outline"}
              onClick={() => handlePrediction("tails")}
              disabled={isFlipping}
              className="w-32"
            >
              <TrendingDownIcon className="w-4 h-4 mr-2" />
              Tails
            </Button>
          </div>

          {/* Flip Button */}
          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={flipCoin}
              disabled={isFlipping || !prediction}
              className="w-48"
            >
              {isFlipping ? "Flipping..." : "Flip Coin"}
            </Button>
          </div>
        </CardContent>
        <CardFooter>
          <Alert>
            <AlertDescription>
              Just like coin flips, investment outcomes can be unpredictable in the short term. 
              This game demonstrates why it's important to make informed decisions and think long-term.
            </AlertDescription>
          </Alert>
        </CardFooter>
      </Card>
    </div>
  );
};